import { useCallback } from 'react';
import NiceModal from '@ebay/nice-modal-react';
import { message } from 'antd';

import { OrbitMutationFailure, updateOrbitSession } from 'api/orbit';
import useMultiLanguageTranslation from 'hooks/useMultiLanguageTranslation';

import { TOrbit } from './i18n/TModules';
import OrbitRenameChatModal from './page/components/OrbitRenameChatModal';
import { OrbitSession, OrbitWorkspaceTag } from './types';

export interface IOrbitChatActions {
  /** Opens "Rename chat" for the chat. */
  rename: (session: OrbitSession) => void;
  setPinned: (session: OrbitSession, pinned: boolean) => Promise<void>;
  /** Files the chat in a Workspace, or sends it back to Recent with `null`. */
  file: (session: OrbitSession, workspace: OrbitWorkspaceTag | null) => Promise<void>;
  /** Says why a change did not go: the chat is gone, or the change failed and nothing moved. */
  reportFailure: (failure: OrbitMutationFailure) => void;
}

/** Rename, pin and Workspace changes from any chat menu. The lists follow the server's answer. */
const useOrbitChatActions = (): IOrbitChatActions => {
  const { t } = useMultiLanguageTranslation();

  const reportFailure = useCallback(
    (failure: OrbitMutationFailure) => {
      message.error(
        t(`${TOrbit}.page.chatUpdate.${failure.kind === 'notFound' ? 'gone' : 'failed'}`)
      );
    },
    [t]
  );

  const setPinned = useCallback(
    async (session: OrbitSession, pinned: boolean) => {
      const outcome = await updateOrbitSession(session.session_id, { pinned });

      if (outcome.kind !== 'ok') {
        reportFailure(outcome);
      }
    },
    [reportFailure]
  );

  const file = useCallback(
    async (session: OrbitSession, workspace: OrbitWorkspaceTag | null) => {
      const outcome = await updateOrbitSession(session.session_id, { workspace });

      if (outcome.kind !== 'ok') {
        reportFailure(outcome);
      }
    },
    [reportFailure]
  );

  const rename = useCallback(
    (session: OrbitSession) => {
      NiceModal.show(OrbitRenameChatModal, {
        title: session.title,
        onConfirm: (title: string | null) => updateOrbitSession(session.session_id, { title }),
        onFailure: reportFailure
      });
    },
    [reportFailure]
  );

  return { rename, setPinned, file, reportFailure };
};

export default useOrbitChatActions;
